import { useEffect, useRef, useState } from "react";
import { LABELS, type CyclePhase, type SessionState } from "@match/shared";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { api, errorMessage, type BurnPreview } from "@/lib/api";
import { money, percent, sentence } from "@/lib/format";
import { useRefreshDirectory } from "@/lib/queries";
import { decimalInput } from "@/lib/units";

/** Barra di avanzamento con etichetta e valori. `ratio` null = non applicabile. */
export function Progress({
  label,
  ratio,
  detail,
  tone = "neutral",
  approximate = false,
  approximateTitle,
}: {
  label: string;
  ratio: number | null;
  detail: string;
  tone?: "neutral" | "prop" | "broker" | "gain";
  /** Numero stimato: lo si dichiara, in modo discreto. */
  approximate?: boolean;
  /** Perché è una stima; default: la spiegazione del DD approssimato. */
  approximateTitle?: string;
}) {
  const fill = { neutral: "bg-muted", prop: "bg-prop", broker: "bg-broker", gain: "bg-gain" }[tone];
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-faint">
          {label}
          {approximate && <ApproximateNote title={approximateTitle} />}
        </span>
        <span className="truncate text-muted">{ratio === null ? "—" : detail}</span>
      </div>
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={ratio === null ? undefined : Math.round(ratio * 100)}
      >
        {ratio !== null && <div className={`h-full rounded-full ${fill}`} style={{ width: `${Math.round(ratio * 100)}%` }} />}
      </div>
    </div>
  );
}

export const DD_APPROXIMATE_EXPLANATION =
  "Stima dal P&L chiuso del prop: il massimo raggiunto e il saldo a fine giornata, da cui con questa base dipende il DD reale, non sono ancora tracciati.";

/** Annotazione discreta, non un allarme: il numero è una stima. */
export function ApproximateNote({ title = DD_APPROXIMATE_EXPLANATION }: { title?: string }) {
  return (
    <span className="ml-1 text-faint" title={title}>
      · stima
    </span>
  );
}

/**
 * Stato della sessione. "Conto bruciato" NON è rosso: nel modello Match è
 * l'esito profittevole, e il colore non deve suggerire un guasto.
 */
export function SessionStateLabel({ state }: { state: SessionState }) {
  const tone = {
    ACTIVE: "border-gain/40 text-gain",
    PAUSED: "border-warn/40 text-warn",
    /*
     * In attesa di payout è uno STATO in cui non si opera, e si vede: il
     * periodo è chiuso, il tratto nuovo non è ancora nato, e chi apre
     * l'operazione può non essere chi ha chiesto il payout. Più marcato della
     * pausa proprio per non confondersi con lei.
     */
    AWAITING_PAYOUT: "border-warn/60 bg-warn/10 text-warn",
    COMPLETED: "border-line text-ink",
    FAILED: "border-line text-ink",
  }[state];
  return <span className={`rounded border px-1.5 py-0.5 text-xs ${tone}`}>{LABELS.sessionState[state]}</span>;
}

export const PHASE_LABEL = (p: CyclePhase) => LABELS.cyclePhase[p];

/**
 * Importo che dipende da una conversione o da dati che possono mancare. Null
 * si mostra come trattino col motivo, un tasso assunto come «≈ · stima».
 */
export function ConvertedAmount({ value, signed = false, missingHint }: { value: { amount: number | null; currency: string; fxMissing: boolean; fxAssumed: boolean }; signed?: boolean; missingHint?: string }) {
  if (value.amount === null) {
    const why = value.fxMissing ? "Tasso di cambio broker -> prop mancante: l'importo non è calcolabile" : (missingHint ?? "Non calcolabile senza quotazioni");
    return (
      <span className="text-faint" title={why}>
        — <span className="text-xs">{value.fxMissing ? "tasso mancante" : "n.d."}</span>
      </span>
    );
  }
  return (
    <span>
      <span className={signed ? (value.amount > 0 ? "text-gain" : value.amount < 0 ? "text-loss" : "text-muted") : ""}>
        {value.fxAssumed ? "≈ " : ""}
        {money(value.amount, value.currency)}
      </span>
      {value.fxAssumed && <ApproximateNote title="Convertito a un tasso assunto (riferimento BCE), non al cambio del conto." />}
    </span>
  );
}

/**
 * Fee pagate del ciclo, in valuta del ciclo. Marcate come stima quando non
 * vengono (tutte) da righe del registro convertite col cambio del conto.
 */
export function FeesPaid({ value, currency, info }: { value: string | number; currency: string; info: { fromLedger: boolean; fxAssumed: boolean } | undefined }) {
  const estimate = !info?.fromLedger || info.fxAssumed;
  return (
    <span>
      {estimate ? "≈ " : ""}
      {money(value, currency)}
      {estimate && (
        <ApproximateNote
          title={
            !info?.fromLedger
              ? "Fee del preset senza riga nel registro: la valuta in cui è stata pagata non è verificata."
              : "Almeno una fee è convertita col riferimento BCE o registrata prima del registro."
          }
        />
      )}
    </span>
  );
}

/** Fase successiva, come la calcola il backend: lo si dice prima di confermare. */
export function nextPhaseOf(current: CyclePhase, presetPhases: number | null): CyclePhase | null {
  if (current === "FUNDED") return null;
  if (current === "PHASE_1" && (presetPhases ?? 2) >= 2) return "PHASE_2";
  return "FUNDED";
}

// ---------------------------------------------------------------------------
// Esito: fase superata / conto bruciato
// ---------------------------------------------------------------------------

export function OutcomePanel({
  kind,
  session,
  onClose,
  onDone,
}: {
  kind: "complete" | "fail";
  session: {
    id: string;
    clientName: string;
    phase: CyclePhase;
    presetPhases: number | null;
    brokerPnl: number;
    brokerCurrency: string;
    /** Valuta del conto prop = valuta del ciclo: default della fee di fase, e destinazione del P&L del broker. */
    propCurrency?: string;
    openPairs: number;
    /** Se diversa dalla valuta del broker, la chiusura congela un tasso di cambio. */
    reportingCurrency?: string;
  };
  onClose: () => void;
  onDone?: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [fee, setFee] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [feeCurrency, setFeeCurrency] = useState(session.propCurrency ?? "USD");
  const [feeRate, setFeeRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * L'anteprima della bruciatura, e le due quote che si confermano con lei.
   *
   * Gli importi sono lo STATO: la percentuale e' una comodita' per scriverli in
   * fretta, e quello che parte al backend sono i due numeri. Se l'operatore
   * decide che stavolta al cliente ne lascia 750 invece di 700, e' 750 che va
   * registrato — non 70% riapplicato a un netto che nel frattempo e' cambiato.
   */
  const [preview, setPreview] = useState<BurnPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ client: number; manager: number } | null>(null);
  /** Vero da quando l'operatore tocca un importo: da li' non si ricalcolano piu' da soli. */
  const touched = useRef(false);

  useEffect(() => setError(null), [kind]);

  // L'anteprima si rifa' quando cambia quello che muove i numeri: la rettifica
  // e il tasso. Non a ogni tasto — mezzo secondo di quiete, e una sola in volo.
  useEffect(() => {
    if (kind !== "fail") return;
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const adj = adjustment.trim() ? decimalInput(adjustment.replace(/^-/, "")) : null;
        const rateValue = fxRate.trim() ? decimalInput(fxRate) : null;
        const p = await api.sessions.burnPreview(session.id, {
          ...(adj !== null ? { cycleAdjustment: adjustment.trim().startsWith("-") ? `-${adj}` : adj } : {}),
          ...(rateValue ? { fxRate: rateValue } : {}),
        });
        if (!vivo) return;
        setPreview(p);
        setPreviewError(null);
        // I predefiniti si riallineano al netto finche' nessuno li ha toccati.
        if (!touched.current) setQuote(p.split ? { client: p.split.client, manager: p.split.manager } : null);
      } catch (e) {
        if (vivo) setPreviewError(errorMessage(e));
      }
    }, 500);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [kind, session.id, adjustment, fxRate]);

  const next = nextPhaseOf(session.phase, session.presetPhases);
  // Il P&L del broker entra nel ciclo in valuta prop: il tasso serve se le valute differiscono.
  const askRate = !!session.propCurrency && session.propCurrency !== session.brokerCurrency;
  const isComplete = kind === "complete";
  const blocked =
    session.openPairs > 0
      ? `Ci sono ${session.openPairs} coppie aperte: chiudile prima di registrare l'esito.`
      : isComplete && !next
        ? "In funded non c'è una fase da superare: segna il conto bruciato o registra i payout."
        : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const adj = adjustment.trim() ? decimalInput(adjustment.replace(/^-/, "")) : null;
    if (adjustment.trim() && adj === null) return setError("La rettifica deve essere un numero");
    if (adjustment.trim() && Number(adj) !== 0 && reason.trim().length < 3) return setError("Scrivi il motivo della rettifica");
    const feeValue = fee.trim() ? decimalInput(fee) : null;
    if (fee.trim() && feeValue === null) return setError("La fee deve essere un numero");
    const rateValue = fxRate.trim() ? decimalInput(fxRate) : null;
    if (fxRate.trim() && (rateValue === null || Number(rateValue) <= 0)) return setError("Il tasso deve essere un numero maggiore di zero");

    setBusy(true);
    try {
      const body = {
        ...(isComplete && feeValue
          ? { nextPhaseFee: feeValue, nextPhaseFeeCurrency: feeCurrency, ...(decimalInput(feeRate) ? { nextPhaseFeeFxRate: decimalInput(feeRate)! } : {}) }
          : {}),
        ...(adj !== null ? { cycleAdjustment: adjustment.trim().startsWith("-") ? `-${adj}` : adj, adjustmentReason: reason.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(rateValue ? { fxRate: rateValue } : {}),
      };
      if (isComplete) await api.sessions.complete(session.id, body);
      else
        await api.sessions.fail(session.id, {
          ...body,
          // Gli IMPORTI, e la percentuale solo come contesto: e' quella che
          // l'operatore ha davanti, ricalcolata dai numeri che ha scritto lui.
          ...(quote ? { split: { client: quote.client, manager: quote.manager, pct: pctOf(quote.client, quote.client + quote.manager) } } : {}),
        });
      await refresh();
      onDone?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={isComplete ? "Fase superata" : "Conto bruciato"}
      subtitle={`${session.clientName} · ${PHASE_LABEL(session.phase)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="outcome-form" disabled={busy || !!blocked}>
            {busy ? "Registrazione…" : isComplete ? "Conferma fase superata" : "Conferma conto bruciato"}
          </Button>
        </>
      }
    >
      <form id="outcome-form" onSubmit={submit} className="space-y-4">
        <div className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
          {isComplete ? (
            <>
              Il prop ha superato la fase. Il P&L del broker in questa fase (
              <span className="text-ink">{money(session.brokerPnl, session.brokerCurrency)}</span>) entra nel ciclo, il ciclo passa a{" "}
              <span className="text-ink">{next ? PHASE_LABEL(next) : "—"}</span> e il valore da recuperare si ricalcola: la perdita sul reale
              si somma al recover della fase successiva.
            </>
          ) : (
            <>
              Il prop è bruciato: è l'esito atteso del modello. Il P&L del broker (
              <span className="text-ink">{money(session.brokerPnl, session.brokerCurrency)}</span>) entra nel ciclo e il ciclo si chiude.
            </>
          )}
        </div>

        {blocked && <ErrorNote>{blocked}</ErrorNote>}

        {!isComplete && (
          <BurnSplit
            preview={preview}
            error={previewError}
            quote={quote}
            onQuote={(q) => {
              touched.current = true;
              setQuote(q);
            }}
          />
        )}

        {isComplete && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Fee della fase successiva" hint="Solo se la prop fa pagare la nuova fase. Entra nel registro, convertita.">
                {(id) => <input id={id} inputMode="decimal" value={fee} placeholder="0" onChange={(e) => setFee(e.target.value)} className={inputClass} />}
              </Field>
            </div>
            <Field label="Valuta">
              {(id) => <input id={id} value={feeCurrency} maxLength={3} onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())} className={inputClass} />}
            </Field>
          </div>
        )}
        {isComplete && fee.trim() !== "" && session.reportingCurrency && feeCurrency !== session.reportingCurrency && (
          <Field label={`Tasso fee ${feeCurrency}/${session.reportingCurrency}`} hint="Vuoto = feed del broker, poi BCE. Si scrive se la registrazione lo chiede.">
            {(id) => <input id={id} inputMode="decimal" value={feeRate} placeholder="es. 0.9200" onChange={(e) => setFeeRate(e.target.value)} className={inputClass} />}
          </Field>
        )}

        <Field label="Rettifica di ciclo" hint="Spread, slippage, commissioni non catturate dal gateway. Positiva = costo, negativa = rimborso.">
          {(id) => <input id={id} inputMode="decimal" value={adjustment} placeholder="0" onChange={(e) => setAdjustment(e.target.value)} className={inputClass} />}
        </Field>
        {adjustment.trim() !== "" && Number(adjustment.replace(",", ".")) !== 0 && (
          <Field label="Motivo della rettifica">
            {(id) => <input id={id} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />}
          </Field>
        )}
        {(askRate || /Tasso .* non disponibile/.test(error ?? "")) && (
          <Field
            label={`Tasso ${session.brokerCurrency}/${session.propCurrency ?? "prop"}`}
            hint="Vuoto = feed del broker, poi BCE. Si scrive solo se la chiusura lo chiede: il tasso si congela sulle coppie e non si rivaluta più."
          >
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        <Field label="Nota">
          {(id) => <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

/** La percentuale che i due importi realizzano, come frazione. */
export function pctOf(part: number, total: number): string {
  if (!(total > 0)) return "0";
  return String(Math.round((part / total) * 1_000_000) / 1_000_000);
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Il netto del ciclo e le due quote, prima di confermare la bruciatura.
 *
 * Tre regole, e sono le stesse in tutto il pannello:
 *
 * 1. **ogni importo porta la sua valuta**, che e' quella del ciclo e non si
 *    deduce da nessuna parte: un numero nudo qui vale quanto un numero
 *    sbagliato;
 * 2. **la percentuale sta accanto al suo importo**, mai da sola: da sola non
 *    dice quanti soldi sono, ed e' l'importo che si registra;
 * 3. **se manca il tasso non si mostra un netto**: si dice che manca. Una cifra
 *    stimata qui sarebbe la cifra su cui qualcuno firma uno split.
 */
function BurnSplit({
  preview,
  error,
  quote,
  onQuote,
}: {
  preview: BurnPreview | null;
  error: string | null;
  quote: { client: number; manager: number } | null;
  onQuote: (q: { client: number; manager: number } | null) => void;
}) {
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!preview) return <p className="text-sm text-faint">Calcolo del netto del ciclo…</p>;

  const cur = preview.currency;
  const net = preview.cycleNet;

  /*
   * IL SALDO CHE NON SI E' LETTO, detto qui e non dopo il rifiuto.
   *
   * Il netto del ciclo si misura sulla differenza di saldo del conto broker:
   * senza quel numero non c'e' un netto da mostrare, e la chiusura si
   * rifiutera' con lo stesso messaggio. Meglio saperlo prima di compilare.
   */
  const saldo = preview.balanceProblem && (
    <div className="rounded border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
      {sentence(preview.balanceProblem.message)} Il netto del tratto si misura sul saldo letto a posizioni chiuse: finché
      non si legge, la chiusura non registra niente.
    </div>
  );

  if (preview.fxMissing) {
    return (
      <>
        {saldo}
        <div className="rounded border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
          Manca il tasso {preview.fx?.base}/{preview.fx?.quote}: il P&L del broker è{" "}
          <span className="text-ink">{money(preview.brokerPnlNative, preview.brokerCurrency)}</span>, ma senza tasso non diventa un netto in{" "}
          {cur}. Scrivi il tasso qui sotto e il netto compare.
        </div>
      </>
    );
  }

  return (
    <div className="space-y-3 rounded border border-line bg-raised/50 p-3">
      {saldo}
      <dl className="space-y-1 text-sm">
        {/*
          * IL NETTO DEL TRATTO, dal saldo: è il numero che la chiusura
          * scriverà. Il P&L dai deal resta sotto, ed è un'altra misura.
          */}
        {preview.period && (
          <Riga
            label={`Netto del tratto n. ${preview.period.seq}`}
            value={
              preview.period.netCycle === null
                ? "—"
                : `${money(preview.period.netCycle, cur)}${
                    preview.period.currency === cur || preview.period.netNative === null
                      ? ""
                      : ` · ${money(preview.period.netNative, preview.period.currency)}`
                  }`
            }
          />
        )}
        <Riga label="P&L del broker in questa fase" value={money(preview.brokerPnl, cur)} />
        <Riga label="P&L realizzato sul reale" value={money(preview.realRealizedPnl, cur)} />
        <Riga label="Payout incassati" value={money(preview.payoutsReceived, cur)} />
        <Riga label="Fee pagate" value={money(-preview.feesPaid, cur)} />
        <Riga label="Rettifiche" value={money(-preview.adjustments, cur)} />
        <div className="flex items-baseline justify-between border-t border-line pt-1">
          <dt className="text-ink">Netto del ciclo</dt>
          <dd className="font-medium text-ink">{money(net, cur)}</dd>
        </div>
      </dl>

      {net === null || net <= 0 ? (
        <p className="text-xs text-faint">
          Niente da dividere: lo split nasce da un netto positivo. Il ciclo si chiude lo stesso, e il netto resta scritto su di lui.
        </p>
      ) : (
        <SplitEditor net={net} currency={cur} quote={quote} defaultPct={preview.burnSplitPct} onQuote={onQuote} />
      )}
    </div>
  );
}

/**
 * LE DUE QUOTE DI UN NETTO, modificabili. Una sola implementazione per le due
 * strade che generano uno split — la bruciatura e il payout: due copie
 * divergerebbero il giorno in cui una impara qualcosa che l'altra non sa.
 *
 * Gli IMPORTI sono lo stato, la percentuale è una comodità per scriverli in
 * fretta: se l'operatore decide che stavolta al cliente ne lascia 750 invece di
 * 700, è 750 che va registrato, non 70% riapplicato a un netto che nel
 * frattempo è cambiato. Le due quote sommano sempre al netto, o il backend
 * rifiuta (`SPLIT_MISMATCH`).
 */
export function SplitEditor({
  net,
  currency,
  quote,
  defaultPct,
  onQuote,
}: {
  net: number;
  currency: string;
  quote: { client: number; manager: number } | null;
  /** La percentuale CONGELATA sul ciclo: il punto di partenza, non un vincolo. */
  defaultPct: number;
  onQuote: (q: { client: number; manager: number }) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <QuotaField
          label="Quota cliente"
          currency={currency}
          value={quote?.client ?? 0}
          pct={pctOf(quote?.client ?? 0, net)}
          max={net}
          onChange={(v) => onQuote({ client: r2(v), manager: r2(net - v) })}
        />
        <PctField
          value={quote ? (quote.client / net) * 100 : defaultPct * 100}
          onChange={(p) => {
            const client = r2((net * p) / 100);
            onQuote({ client, manager: r2(net - client) });
          }}
        />
      </div>
      <QuotaField
        label="Quota nostra"
        currency={currency}
        value={quote?.manager ?? 0}
        pct={pctOf(quote?.manager ?? 0, net)}
        max={net}
        onChange={(v) => onQuote({ client: r2(net - v), manager: r2(v) })}
      />
      <p className="text-xs text-faint">
        Si registrano gli IMPORTI, non la percentuale: {percent(Number(pctOf(quote?.client ?? 0, net)), 1)} è quello che questi numeri
        realizzano su {money(net, currency)}, ed è lì per farli scrivere in fretta. Le due quote sommano sempre al netto.
      </p>
    </div>
  );
}

function Riga({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

/**
 * Un campo numerico che mentre lo si scrive resta quello che si sta scrivendo.
 *
 * Il valore vero e' un numero e arriva da fuori — l'altro campo lo muove — ma
 * il testo digitato e' suo finche' il campo ha il fuoco: rimandare indietro
 * `String(numero)` a ogni tasto cancella lo «0,» appena battuto e non si riesce
 * piu' a scrivere un decimale.
 */
function NumberInput({ id, value, max, onChange, className }: { id: string; value: number; max?: number; onChange: (v: number) => void; className?: string }) {
  const [txt, setTxt] = useState(String(value));
  const [scrivo, setScrivo] = useState(false);
  useEffect(() => {
    if (!scrivo) setTxt(String(value));
  }, [value, scrivo]);
  return (
    <input
      id={id}
      inputMode="decimal"
      value={txt}
      onFocus={() => setScrivo(true)}
      onBlur={() => setScrivo(false)}
      onChange={(e) => {
        setTxt(e.target.value);
        const n = Number(e.target.value.replace(",", "."));
        if (Number.isFinite(n) && n >= 0 && (max === undefined || n <= max)) onChange(n);
      }}
      className={className ?? inputClass}
    />
  );
}

/** Un importo con la sua valuta scritta accanto, e la percentuale che realizza. */
function QuotaField({
  label,
  currency,
  value,
  pct,
  max,
  onChange,
}: {
  label: string;
  currency: string;
  value: number;
  pct: string;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={`${label} (${currency})`} hint={`${percent(Number(pct), 1)} del netto`}>
      {(id) => <NumberInput id={id} value={value} max={max} onChange={onChange} />}
    </Field>
  );
}

function PctField({ value, onChange }: { value: number; onChange: (p: number) => void }) {
  return (
    <Field label="%" hint="al cliente">
      {(id) => <NumberInput id={id} value={Math.round(value * 100) / 100} max={100} onChange={onChange} className={`${inputClass} w-24`} />}
    </Field>
  );
}
